import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import CastFigure from '../../motion/CastFigure';
import { MOTION_TIMING, useInView, useScrollProgress } from '../../motion';
import { castKey, hasPose } from '../../data/cast';
import Depth from '../../motion/Depth';
import RegistrarNumber from './RegistrarNumber';

const clamp = (value) => Math.min(1, Math.max(0, value));
const segment = (value, start, end) => clamp((value - start) / (end - start));
const ease = (value) => 1 - ((1 - value) ** 3);

export const TIER_POSE_SEQUENCES = Object.freeze({
  a: ['smug', 'celebrate', 'smug'],
  b: ['point', 'base'],
  c: ['shock', 'smug'],
  d: ['shock', 'facepalm'],
  f: ['shock', 'facepalm', 'shock', 'facepalm'],
});

export const MANAGER_POSE_OVERRIDES = Object.freeze({
  robby: { settle: 'shrug', fallback: 'facepalm' },
  amir: { settle: 'rage', fallback: 'facepalm' },
  'a-j': { settle: 'sob', loop: 'sob', fallback: 'facepalm' },
});

const WEIGHT_LABELS = Object.freeze({
  starterStrength: 'starters',
  totalVorp: 'total VORP',
  draftedEfficiency: 'drafted efficiency',
  keeperValue: 'keeper value',
  benchQuality: 'bench',
  byeResilience: 'byes',
  positionalConstruction: 'construction',
});

function availablePose(team, preferred, fallback = 'base') {
  const key = castKey(team.manager);
  if (hasPose(key, preferred)) return preferred;
  if (hasPose(key, fallback)) return fallback;
  return 'base';
}

export function settlePoseFor(team) {
  const override = MANAGER_POSE_OVERRIDES[team.slug];
  if (override?.settle) return availablePose(team, override.settle, override.fallback);
  if (team.tier === 'a') return availablePose(team, 'smug');
  if (team.tier === 'b') return 'base';
  if (team.tier === 'c') return availablePose(team, 'smug');
  if (team.tier === 'd') return availablePose(team, 'facepalm');
  if (team.tier === 'f') {
    const preferred = override?.loop || 'facepalm';
    return availablePose(team, preferred, override?.fallback || 'facepalm');
  }
  return 'base';
}

function ParticleBurst({ tier, burst, onDone }) {
  const settings = tier === 'a'
    ? { count: 18, duration: 2.4, spread: 1.2, className: 'rg-fx--gold' }
    : tier === 'f'
      ? { count: 14, duration: 3.2, spread: 1.2, className: 'rg-fx--grey' }
      : tier === 'd'
        ? { count: 3, duration: 3.2, spread: 0.8, className: 'rg-fx--grey' }
        : null;
  const ended = useRef(0);

  if (!settings || !burst) return null;

  const particles = Array.from({ length: settings.count }, (_, index) => {
    const direction = index % 2 === 0 ? 1 : -1;
    const rotation = direction * (180 + ((index * 137) % 360));
    const delay = (index * 73) % 400;
    const duration = settings.duration + (((index * 17) % 100) / 100) * settings.spread;
    const drift = direction * (tier === 'a' ? 24 + ((index * 19) % 58) : 8 + ((index * 11) % 18));
    return (
      <i
        key={index}
        style={{
          '--x': `${(index * 37) % 100}%`,
          '--r': `${rotation}deg`,
          '--dur': `${duration.toFixed(2)}s`,
          '--delay': `${delay}ms`,
          '--drift': `${drift}px`,
        }}
      />
    );
  });

  const finishOne = () => {
    ended.current += 1;
    if (ended.current === settings.count) onDone();
  };

  return (
    <div className={`rg-fx ${settings.className}`} onAnimationEnd={finishOne} aria-hidden="true" data-burst={burst}>
      {particles}
    </div>
  );
}

function Metric({ label, player, className = '', children }) {
  return (
    <div className="rg-metric">
      <span className="rg-metric__label">{label}</span>
      <strong className="rg-metric__player">{player}</strong>
      <span className={`rg-metric__value ${className}`}>{children}</span>
    </div>
  );
}

function GradeCard({ team, copy, weights, totalTeams, countActive, strikeCount, reduced, recoil, roastOn }) {
  const roastLines = copy.roast.split(/\n\n+/);
  const keeper = team.keeperVerdict[0];
  return (
    <article className={`rg-card paper ${recoil ? 'is-recoiling' : ''}`} data-tier={team.tier}>
      <header className="rg-card__header">
        <span>Rank {team.rank} of {totalTeams}</span>
        <span className="rg-card__score">
          <RegistrarNumber value={team.score} active={countActive} reduced={reduced} precision={2} runKey={strikeCount} /> / 100
        </span>
      </header>

      <Link to={`/draft-grades/${team.slug}`} className="rg-card__identity">
        <h2>{team.team}</h2>
        <span>{team.manager}</span>
      </Link>

      <div className="rg-vitals">
        <Metric label="Best pick" player={team.bestPick.player} className="rg-metric__value--good">
          <RegistrarNumber value={team.bestPick.valueVsSlot} active={countActive} reduced={reduced} signed runKey={strikeCount} />
        </Metric>
        <Metric label="Worst pick" player={team.worstPick.player} className={team.worstPick.valueVsSlot < -100 ? 'rg-metric__value--bad' : ''}>
          <RegistrarNumber value={team.worstPick.valueVsSlot} active={countActive} reduced={reduced} signed runKey={strikeCount} />
        </Metric>
        <Metric label="Keeper price" player={keeper.player}>
          {keeper.label.split(' / ')[0]}
        </Metric>
        <Metric label="Bench" player="Quality">
          <RegistrarNumber value={team.benchQuality} active={countActive} reduced={reduced} runKey={strikeCount} />
        </Metric>
      </div>

      {team.byePileups.map((pileup) => (
        <div className="rg-bye" key={`${pileup.week}-${pileup.count}`}>
          <strong>Week {pileup.week} — {pileup.count} starters out</strong>
          <span>{pileup.players.join(' · ')}</span>
        </div>
      ))}

      <div className="rg-roast">
        <p className={`rg-caption rg-line ${roastOn[0] ? 'is-on' : ''}`}>{copy.caption}</p>
        {roastLines.map((line, index) => (
          <p key={line} className={`rg-line ${roastOn[index + 1] ? 'is-on' : ''}`}>{line}</p>
        ))}
      </div>

      <footer className="rg-card__footer">
        <a href="#registrar-receipts">
          Composite {team.score.toFixed(2)} · {Object.entries(weights).map(([label, weight]) => `${WEIGHT_LABELS[label] || label} ${Math.round(weight * 100)}%`).join(' · ')}
        </a>
      </footer>
    </article>
  );
}

function RegistrarBeat({ team, copy, weights, totalTeams, reduced, stacked, onCurrent }) {
  const [pose, setPose] = useState(reduced ? settlePoseFor(team) : 'base');
  const [struck, setStruck] = useState(reduced);
  const [strikeCount, setStrikeCount] = useState(reduced ? 1 : 0);
  const [recoil, setRecoil] = useState(false);
  const [burst, setBurst] = useState(reduced ? 0 : null);
  const [countActive, setCountActive] = useState(reduced);
  const [roastOn, setRoastOn] = useState(() => Array(4).fill(reduced));
  const armed = useRef(true);
  const timers = useRef([]);
  const ghost = useRef(null);
  const cardShell = useRef(null);
  const figureShell = useRef(null);
  const [loadRef, inView] = useInView({ threshold: 0, rootMargin: '100% 0px', once: true });
  const [stackedRef, stackedInView] = useInView({ threshold: 0.25, rootMargin: '0px', once: true });
  const fireStampRef = useRef(null);

  const clearTimers = () => {
    timers.current.forEach((timer) => clearTimeout(timer));
    timers.current = [];
  };

  useEffect(() => () => {
    timers.current.forEach((timer) => clearTimeout(timer));
    timers.current = [];
  }, []);

  useEffect(() => {
    if (!reduced) return;
    setPose(settlePoseFor(team));
    setStruck(true);
    setCountActive(true);
    setRoastOn(Array(4).fill(true));
  }, [reduced, team]);

  const later = (fn, delay) => {
    const timer = setTimeout(fn, delay);
    timers.current.push(timer);
    return timer;
  };

  const startPoseSequence = () => {
    const step = MOTION_TIMING.registrarTierStepMs;
    const loop = MOTION_TIMING.registrarLoopMs;
    const sequence = TIER_POSE_SEQUENCES[team.tier] || ['base'];
    clearTimers();

    if (team.tier === 'a') {
      setPose(availablePose(team, sequence[0]));
      later(() => setPose(availablePose(team, sequence[1], sequence[0])), step + 100);
      later(() => setPose(availablePose(team, sequence[2], sequence[0])), step * 3);
      return;
    }
    if (team.tier === 'b') {
      setPose(availablePose(team, sequence[0]));
      later(() => setPose(availablePose(team, sequence[1])), step * 2);
      return;
    }
    if (team.tier === 'c') {
      setPose(availablePose(team, sequence[0]));
      later(() => setPose(availablePose(team, sequence[1])), step + 300);
      return;
    }
    if (team.tier === 'd') {
      const override = MANAGER_POSE_OVERRIDES[team.slug];
      setPose(availablePose(team, sequence[0]));
      later(() => setPose(availablePose(team, override?.settle || sequence[1], override?.fallback || sequence[1])), step);
      if (team.slug === 'robby') {
        later(() => setPose(availablePose(team, 'shrug', 'facepalm')), (step * 2) + 1000);
      }
      return;
    }

    const override = MANAGER_POSE_OVERRIDES[team.slug];
    const second = availablePose(team, override?.loop || sequence[1], override?.fallback || sequence[1]);
    const shortStep = step - 100;
    const first = availablePose(team, sequence[0]);
    setPose(first);
    later(() => setPose(second), shortStep);
    later(() => setPose(first), loop);
    const repeat = (nextPose) => {
      setPose(nextPose);
      later(() => repeat(nextPose === second ? first : second), loop);
    };
    later(() => repeat(second), loop + shortStep);
  };

  const fireStamp = (settleOnly = false) => {
    armed.current = false;
    setStruck(true);
    setStrikeCount((count) => count + 1);
    later(() => {
      setRecoil(true);
      setBurst((value) => (value || 0) + 1);
      if (settleOnly) setPose(settlePoseFor(team));
      else startPoseSequence();
      later(() => setRecoil(false), 260);
    }, MOTION_TIMING.registrarStampToPoseMs);
  };
  fireStampRef.current = fireStamp;

  useEffect(() => {
    if (!stacked || reduced || !stackedInView || !armed.current) return;
    fireStampRef.current?.(true);
    setCountActive(true);
    setRoastOn(Array(4).fill(true));
  }, [reduced, stacked, stackedInView]);

  const progressRef = useScrollProgress((progress, visible) => {
    if (!visible || reduced) return;
    if (progress < 0.12 && !armed.current) {
      armed.current = true;
      clearTimers();
      setStruck(false);
      setPose('base');
      setBurst(null);
      setCountActive(false);
      setRecoil(false);
    }
    if (progress >= 0.26 && armed.current) fireStamp();
    if (progress >= 0.3) setCountActive(true);

    const ghostIn = ease(segment(progress, 0, 0.1));
    const cardIn = ease(segment(progress, 0, 0.1));
    const figureIn = ease(segment(progress, 0.1, 0.2));
    const exitStart = window.matchMedia?.('(max-width: 900px)').matches ? 0.88 : 0.8;
    const exiting = ease(segment(progress, exitStart, 1));

    if (ghost.current) {
      ghost.current.style.opacity = String(ghostIn * 0.08);
      ghost.current.style.transform = `translateY(${24 * (1 - ghostIn)}px)`;
    }
    if (cardShell.current) {
      cardShell.current.style.opacity = String(cardIn * (1 - (exiting * 0.65)));
      cardShell.current.style.transform = `translateY(${(40 * (1 - cardIn)) - (28 * exiting)}px)`;
    }
    if (figureShell.current) {
      figureShell.current.style.opacity = String(figureIn * (1 - (exiting * 0.65)));
      figureShell.current.style.transform = `translateY(calc(${12 * (1 - figureIn)}vh - ${32.2 * exiting}px)) scale(${0.7 + (0.3 * figureIn)})`;
    }

    const nextRoast = Array.from({ length: 4 }, (_, index) => progress >= 0.5 + (index * 0.04));
    setRoastOn((current) => current.every((value, index) => value === nextRoast[index]) ? current : nextRoast);
    if (progress > 0 && progress < 1) onCurrent(team.rank);
  }, { enabled: !reduced && !stacked });

  const setBeatRef = (node) => {
    progressRef.current = node;
    loadRef.current = node;
    stackedRef.current = node;
  };

  return (
    <section
      id={`grade-${team.slug}`}
      ref={setBeatRef}
      className={`rg-beat ${stacked ? 'rg-beat--stacked' : ''}`}
      data-tier={team.tier}
      data-rank={team.rank}
      data-strike-count={strikeCount}
      data-testid={`grade-beat-${team.slug}`}
    >
      <Depth className="rg-stage" strength={0.55}>
        <div className="rg-figure-stage">
          <span ref={ghost} className="rg-ghost" aria-hidden="true">{team.rank}</span>
          <div ref={figureShell} className="rg-figure-shell">
            <div data-depth="0.6">
              {(inView || reduced) && (
                <CastFigure name={team.manager} pose={pose} trigger="none" className="rg-figure" alt={`${team.manager} reacts to grade ${team.letter}`} />
              )}
            </div>
          </div>
          <div className={`rg-stamp ${struck ? 'is-struck' : ''}`} data-depth="0.2" aria-label={`${team.manager} grade ${team.letter}`}>
            <span>{team.letter}</span>
          </div>
          {!reduced && <ParticleBurst key={burst} tier={team.tier} burst={burst} onDone={() => setBurst(null)} />}
        </div>

        <div ref={cardShell} className="rg-card-shell">
          <div data-depth="-0.4">
            <GradeCard
              team={team}
              copy={copy}
              weights={weights}
              totalTeams={totalTeams}
              countActive={countActive}
              strikeCount={strikeCount}
              reduced={reduced}
              recoil={recoil}
              roastOn={roastOn}
            />
          </div>
        </div>
      </Depth>
    </section>
  );
}

function useStackedCountdown() {
  const [stacked, setStacked] = useState(() => (
    typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(max-width: 900px)').matches
  ));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia('(max-width: 900px)');
    const update = () => setStacked(query.matches);
    update();
    query.addEventListener ? query.addEventListener('change', update) : query.addListener(update);
    return () => (query.removeEventListener ? query.removeEventListener('change', update) : query.removeListener(update));
  }, []);
  return stacked;
}

export default function RegistrarCountdown({ teams, copyBySlug, weights, reduced }) {
  const [currentRank, setCurrentRank] = useState(teams[0]?.rank);
  const stacked = useStackedCountdown();
  return (
    <section id="registrar-countdown" className={`rg-countdown ${stacked ? 'rg-countdown--stacked' : ''}`} aria-label="Draft grade countdown">
      {stacked ? (
        <a className="rg-mobile-standings" href="#registrar-ledger">Standings ↓</a>
      ) : (
        <div className="rg-controls">
          <a href="#registrar-ledger">Skip to the standings</a>
          <span>{currentRank} / {teams.length} → 1 / {teams.length}</span>
        </div>
      )}
      {teams.map((team) => (
        <RegistrarBeat
          key={team.slug}
          team={team}
          copy={copyBySlug[team.slug === 'a-j' ? 'aj' : team.slug]}
          weights={weights}
          totalTeams={teams.length}
          reduced={reduced}
          stacked={stacked}
          onCurrent={setCurrentRank}
        />
      ))}
    </section>
  );
}
